import React from 'react';
import { Link } from 'react-router-dom';
import { Home, FileText, Mail, Phone, MapPin, Building2 } from 'lucide-react';
import { COMPANY } from '../lib/company';

const Terms = () => {
  const sections = [
    {
      title: '1. Introducere',
      content: (
        <>
          <p>
            Prezentul document stabilește termenii și condițiile de utilizare a site-ului <strong>AXA</strong> („Site-ul”) și condițiile aplicabile comenzilor plasate online. Prin accesarea Site-ului sau plasarea unei comenzi, confirmi că ai citit, ai înțeles și accepți acești Termeni și Condiții.
          </p>
          <p>
            <strong>Vânzător / Comerciant:</strong> {COMPANY.name} („Vânzătorul”).
          </p>
          <div className="mt-4 bg-neutral-50 border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-neutral-900">Datele complete ale firmei</span>
            </div>
            <ul className="space-y-2 text-sm text-neutral-700">
              <li><strong>Denumire:</strong> {COMPANY.name}</li>
              <li><strong>Cod Unic de Înregistrare (CUI):</strong> {COMPANY.cui}</li>
              <li><strong>Nr. de ordine în Registrul Comerțului:</strong> {COMPANY.regCom}</li>
              <li><strong>Identificator Unic European (EUID):</strong> {COMPANY.euid}</li>
              <li><strong>Sediu social:</strong> {COMPANY.sediu}</li>
              <li><strong>Activitate principală (CAEN):</strong> {COMPANY.caen}</li>
              <li><strong>Director:</strong> {COMPANY.director}</li>
              <li><strong>Certificat înregistrare:</strong> {COMPANY.certificat} · Data eliberării: {COMPANY.dataEliberare}</li>
              <li className="pt-2 border-t border-neutral-200 mt-3">
                <strong>Email:</strong> <a href={`mailto:${COMPANY.email}`} className="text-amber-700 hover:underline">{COMPANY.email}</a>
              </li>
              <li>
                <strong>Telefon:</strong> <a href={`tel:${COMPANY.phoneClean}`} className="text-amber-700 hover:underline">{COMPANY.phone}</a>
              </li>
            </ul>
          </div>
        </>
      ),
    },
    {
      title: '2. Definiții',
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Client/Consumator:</strong> orice persoană fizică ce acționează în scopuri din afara activității sale comerciale/profesionale.</li>
          <li><strong>Cumpărător:</strong> orice persoană fizică sau juridică ce plasează o comandă.</li>
          <li><strong>Produse:</strong> bunurile comercializate pe Site.</li>
          <li><strong>Contract la distanță:</strong> contract încheiat între Vânzător și Cumpărător fără prezență fizică simultană.</li>
        </ul>
      ),
    },
    {
      title: '3. Informații despre produse',
      content: (
        <p>
          Imaginile au caracter informativ. Pot exista diferențe minore de culoare/ambalaj în funcție de lot, producător sau setările dispozitivului. Vânzătorul depune eforturi rezonabile pentru descrieri corecte și complete.
        </p>
      ),
    },
    {
      title: '4. Prețuri și facturare',
      content: (
        <>
          <p>Prețurile sunt afișate în lei (RON) și includ TVA, dacă este aplicabil. Costurile de livrare sunt afișate separat înainte de finalizarea comenzii.</p>
          <p>Factura se transmite în format electronic la adresa de e-mail indicată în comandă (sau prin contul de client). Pentru întrebări legate de facturare, contactați-ne la <a href={`mailto:${COMPANY.email}`} className="text-amber-700 underline">{COMPANY.email}</a> sau la <a href={`tel:${COMPANY.phoneClean}`} className="text-amber-700 underline">{COMPANY.phone}</a>.</p>
        </>
      ),
    },
    {
      title: '5. Comanda – pași și încheierea contractului',
      content: (
        <p>
          Plasarea comenzii pe Site reprezintă o ofertă de cumpărare. Contractul se consideră încheiat în momentul confirmării comenzii de către Vânzător (prin e-mail / status comandă). Vânzătorul poate refuza o comandă în mod justificat (ex.: stoc epuizat, erori tehnice de preț, date incomplete).
        </p>
      ),
    },
    {
      title: '6. Modalități de plată',
      content: (
        <p>
          Metodele de plată disponibile sunt afișate la checkout: ramburs la livrare, card online, transfer bancar. Dacă plata este online, procesarea se face printr-un procesator de plăți autorizat; datele de card nu sunt stocate de Vânzător.
        </p>
      ),
    },
    {
      title: '7. Livrare',
      content: (
        <p>
          Termenele de livrare sunt estimative (24-48h în zilele lucrătoare). Riscul asupra produselor trece la momentul predării către Client/Consumator. Pentru detalii suplimentare ne puteți contacta la <a href={`tel:${COMPANY.phoneClean}`} className="text-amber-700 underline">{COMPANY.phone}</a>.
        </p>
      ),
    },
    {
      title: '8. Dreptul de retragere (pentru consumatori)',
      content: (
        <>
          <p>
            Consumatorul beneficiază de <strong>14 zile</strong> pentru a se retrage din contract, fără a invoca un motiv, conform OUG 34/2014 privind contractele la distanță.
          </p>
          <p>
            Pentru exercitarea dreptului de retragere, trimiteți o notificare la <a href={`mailto:${COMPANY.email}`} className="text-amber-700 underline">{COMPANY.email}</a> sau telefonic la <a href={`tel:${COMPANY.phoneClean}`} className="text-amber-700 underline">{COMPANY.phone}</a>.
          </p>
        </>
      ),
    },
    {
      title: '9. Garanții și reclamații',
      content: (
        <p>
          Produsele beneficiază de garanția legală de conformitate, conform legislației aplicabile. Pentru reclamații, ne puteți contacta la <a href={`mailto:${COMPANY.email}`} className="text-amber-700 underline">{COMPANY.email}</a> sau la telefon <a href={`tel:${COMPANY.phoneClean}`} className="text-amber-700 underline">{COMPANY.phone}</a>.
        </p>
      ),
    },
    {
      title: '10. Răspundere',
      content: (
        <>
          <p>Vânzătorul nu răspunde pentru:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>utilizarea necorespunzătoare a produselor;</li>
            <li>indisponibilități temporare ale Site-ului din motive tehnice;</li>
            <li>prejudicii indirecte (ex.: pierderi de profit), în măsura permisă de lege.</li>
          </ul>
        </>
      ),
    },
    {
      title: '11. Proprietate intelectuală',
      content: (
        <p>
          Conținutul Site-ului (texte, logo, elemente grafice) este protejat de drepturi de proprietate intelectuală. Orice utilizare neautorizată este interzisă.
        </p>
      ),
    },
    {
      title: '12. Cont de client',
      content: (
        <p>
          Clientul este responsabil pentru confidențialitatea datelor de autentificare și pentru activitatea desfășurată în cont.
        </p>
      ),
    },
    {
      title: '13. Forță majoră',
      content: (
        <p>
          Niciuna dintre părți nu răspunde pentru neexecutare dacă aceasta este cauzată de forță majoră, conform legii.
        </p>
      ),
    },
    {
      title: '14. Soluționarea litigiilor. Legea aplicabilă',
      content: (
        <>
          <p>
            Contractul este guvernat de legea română. Se va încerca soluționarea amiabilă; dacă nu este posibil, litigiile se soluționează de instanțele competente.
          </p>
          <p>
            Consumatorii au dreptul să apeleze la procedurile alternative de soluționare a litigiilor (SAL) prin <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">ANPC</a> sau platforma europeană <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">ODR</a>.
          </p>
        </>
      ),
    },
    {
      title: '15. Contact',
      content: (
        <div className="space-y-2">
          <p>Pentru orice întrebare legată de comenzi, produse sau acești Termeni și Condiții, ne puteți contacta:</p>
          <ul className="space-y-1.5 mt-3">
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-amber-600" /> <a href={`mailto:${COMPANY.email}`} className="text-amber-700 hover:underline font-medium">{COMPANY.email}</a></li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-amber-600" /> <a href={`tel:${COMPANY.phoneClean}`} className="text-amber-700 hover:underline font-medium">{COMPANY.phone}</a></li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-amber-600 mt-0.5" /> <span>{COMPANY.sediu}</span></li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <div className="bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm flex items-center gap-2 text-neutral-500">
          <Link to="/" className="hover:text-amber-600 flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Acasă
          </Link>
          <span>/</span>
          <span className="text-neutral-900">Termeni și Condiții</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-neutral-900">Termeni și Condiții</h1>
        </div>
        <p className="text-neutral-500 mb-10">
          Ultima actualizare: 18 ianuarie 2026
        </p>

        <div className="space-y-8">
          {sections.map((s, i) => (
            <section key={i} className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8">
              <h2 className="text-xl font-serif font-bold text-neutral-900 mb-3">{s.title}</h2>
              <div className="prose prose-neutral max-w-none text-neutral-700 leading-relaxed space-y-3 text-[15px]">
                {s.content}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <p className="text-sm text-amber-900">
            Pentru întrebări suplimentare:{' '}
            <a href={`mailto:${COMPANY.email}`} className="font-semibold underline">{COMPANY.email}</a>
            {' '}sau{' '}
            <a href={`tel:${COMPANY.phoneClean}`} className="font-semibold underline">{COMPANY.phone}</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
